import { formatISO } from 'date-fns';
import { v4 } from 'uuid';

import { FieldType } from '~/evifields/src/types/FieldType';
import { Filter } from '~/evifields/src/types/Filter';
import {
  ClauseValue,
  DatePeriod,
  DateValue,
  DurationUnit,
} from '~/evifields/src/types/Value';
import {
  BooleanQueryEntity,
  ClauseFilterQueryEntity,
  ClauseTextSearchOperator,
  EnumSetFilterQueryEntity,
  FilterQueryEntity,
} from '~/types/OpenSearch';
import { PilotId } from '~/types/PilotId';

import { SearchFilterRecord, SearchQuery } from '../types';
import { testIsFilter } from './query';

type DateQueryValue = string | number | DurationUnit;

export const toEntity = (filter: Filter, type: FieldType) => {
  switch (type) {
    case 'enum_set':
      return toEnumSetEntity(filter);
    case 'clause_v2':
    case 'clause':
      return toClauseEntity(filter as Filter<ClauseValue>);
    case 'date':
      return toDateEntity(filter as Filter<DateValue>);
    case 'bool_text_search':
      return {
        type: 'bool_text_search',
        value: filter.values[0],
      } as BooleanQueryEntity;
    default:
      const filterEntity: FilterQueryEntity = {
        type: 'filter',
        id: filter.fieldId,
        operator: filter.operatorId!,
        value: {
          value_list: [],
          value_meta: {},
        },
      };

      filterEntity.value = {
        value_list: filter.values,
        value_meta: {},
      };
      return filterEntity;
  }
};

const toDateEntity = (filter: Filter<DateValue>) => {
  const filterEntity: FilterQueryEntity<DateQueryValue> = {
    type: 'filter',
    id: filter.fieldId,
    operator: filter.operatorId!,
    value: {
      value_list: [],
      value_meta: {},
    },
  };
  const dateValue = filter.values[0]! as DatePeriod;
  if (dateValue?.unit) {
    filterEntity.value = {
      value_list: [dateValue.value!, dateValue.unit],
      value_meta: {},
    };
  } else {
    filterEntity.value = {
      value_list: filter.values.map((date) => {
        //l10n-ignore: TECH DEBT - too nested to use useFormatDate directly
        return formatISO(date as Date, { representation: 'date' });
      }),
      value_meta: {},
    };
  }
  return filterEntity;
};

const toEnumSetEntity = <T>(filter: Filter<T>) => {
  const filterEntity: EnumSetFilterQueryEntity<T> = {
    type: 'filter',
    id: filter.fieldId,
    operator: filter.operatorId!,
    value: {
      value_list: [],
      value_meta: {},
    },
  };
  filterEntity.value = {
    value_list: filter.values,
    value_meta: {
      multi_select_data: filter.asyncValue
        ? {
            is_all_selected: filter.asyncValue.isAllSelected,
            length: filter.asyncValue.length,
            search: filter.asyncValue.search,
            selected_values_map: filter.asyncValue.selectedValuesMap,
          }
        : undefined,
      filterView: filter.filterView,
    },
  };
  return filterEntity;
};

const toClauseEntity = (filter: Filter<ClauseValue>) => {
  const filterEntity: ClauseFilterQueryEntity = {
    type: 'filter',
    id: filter.fieldId,
    operator: filter.operatorId!,
    value: {
      value_list: [],
      value_meta: {},
    },
  };
  const clauseValue = filter.values?.length ? filter.values[0] : undefined;
  if (clauseValue) {
    filterEntity.value = {
      value_list: [clauseValue.provision],
      value_meta: {},
    };
    const clauseTextSearch = clauseValue.text_search[0];
    if (clauseTextSearch?.text) {
      filterEntity.value = {
        ...filterEntity.value,
        value_meta: {
          clause_text_search: {
            id: 'text_search',
            operator: clauseTextSearch?.scope.value as ClauseTextSearchOperator, // This is to patch the type Filter<ClauseValue> so we don't have to define a ClauseValueV2 - TODO?
            value: {
              value_list: [clauseTextSearch?.text],
              value_meta: {},
            },
          },
        },
      };
    }
  }
  return filterEntity;
};

export const getDefaultFilters = (
  defaultFilterIds: Array<PilotId | string> = [],
  searchFilters: SearchFilterRecord = {},
): Filter[] =>
  Object.values(searchFilters)
    .filter(
      (searchFilter) =>
        defaultFilterIds.includes(searchFilter.id) ||
        defaultFilterIds.includes(searchFilter.label),
    )
    .map((searchFilter) => ({
      id: v4(),
      fieldId: searchFilter.id,
      operatorId: null,
      values: [],
    }));

export const getEmptyFilters = (query: SearchQuery): FilterQueryEntity[] => {
  return query.filter(
    (q): q is FilterQueryEntity =>
      testIsFilter(q) && !q.operator && !q.value.value_list.length,
  );
};
